import { useCallback, useEffect, useRef, useState } from '@biletiniz/ui-core';
import { useApolloClient } from '@biletiniz/apollo';
import { FLIGHT_SEARCH_SESSION_QUERY } from './gql/flight-search-session';
import {
  FLIGHT_SESSION_WARNING_SECONDS,
  getStoredFlightSessionExpiry,
  syncFlightSearchSessionExpiry,
} from './flight-search-session-storage';

const RESYNC_INTERVAL_MS = 30 * 1000; // 30 seconds in milliseconds

/**
 * Counts down the flight search session. The stored expiry gives an instant
 * value on every page; the server is asked again on mount, every 30 seconds,
 * and whenever the user comes back to the page (tab shown, window focused
 * after another application, restored from the back/forward cache, back
 * online), because a page left in the background may not have run its timers
 * at all. The server's answer always wins.
 *
 * `sync()` resolves to the server status, or null when the server could not be
 * asked, so callers can check the session right before acting on it.
 *
 * `latestExpiresAt` is the expiry from the page's newest search response. A
 * renewed session keeps its id, so a new value here is what restarts the
 * countdown.
 */
export const useFlightSearchSession = (session, latestExpiresAt = null) => {
  const client = useApolloClient();
  const [expiresAt, setExpiresAt] = useState(() =>
    session ? getStoredFlightSessionExpiry(session) : null
  );
  const [now, setNow] = useState(() => Date.now());
  const currentSession = useRef(session);
  currentSession.current = session;

  const sync = useCallback(() => {
    if (!session) return Promise.resolve(null);
    // useLazyQuery (Apollo 3.3) does not re-send a request with unchanged
    // variables, so every sync goes through the client directly.
    return client
      .query({
        query: FLIGHT_SEARCH_SESSION_QUERY,
        variables: { session },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        const status = data?.flightSearchSession;
        if (!status || currentSession.current !== session) return null;
        setExpiresAt(
          syncFlightSearchSessionExpiry(session, status.remainingSeconds)
        );
        setNow(Date.now());
        return status;
      })
      .catch(() => null);
  }, [client, session]);

  useEffect(() => {
    if (!session) {
      setExpiresAt(null);
      return undefined;
    }
    setExpiresAt(getStoredFlightSessionExpiry(session));
    sync();

    const onReturn = () => {
      setNow(Date.now());
      if (document.visibilityState !== 'hidden') sync();
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    window.addEventListener('pageshow', onReturn);
    window.addEventListener('online', onReturn);
    const resync = setInterval(sync, RESYNC_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
      window.removeEventListener('pageshow', onReturn);
      window.removeEventListener('online', onReturn);
      clearInterval(resync);
    };
  }, [session, sync]);

  useEffect(() => {
    if (session && latestExpiresAt) {
      setExpiresAt(latestExpiresAt);
      setNow(Date.now());
    }
  }, [latestExpiresAt]);

  useEffect(() => {
    if (!expiresAt) return undefined;
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const remainingSeconds = expiresAt
    ? Math.max(0, Math.ceil((expiresAt - now) / 1000))
    : null;

  return {
    remainingSeconds,
    isWarning:
      remainingSeconds !== null &&
      remainingSeconds > 0 &&
      remainingSeconds <= FLIGHT_SESSION_WARNING_SECONDS,
    isExpired: remainingSeconds === 0,
    sync,
  };
};
